'use client';

import type { ProductInformation } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import { Samples } from '../ProductCard/Samples';
import { AddToCart } from './AddToCart';
import { scrollToElement } from './scrollToElement';

/** A button that scrolls to a section further down the page. */
export type ScrollCta = {
  /** DOM id for the button, e.g. for analytics hooks. */
  id?: string;
  label: string;
  /** Id of the section to scroll to (`visualizer`, `configurator`, …). */
  targetId: string;
  /** Render as the primary (filled) button. */
  primary?: boolean;
};

export type ProductDetailsCtasProps = {
  productInfo: ProductInformation;
  showAddToCart?: boolean;
  showOrderSample?: boolean;
  /** Optional page-specific CTA rendered between samples and request-info. */
  scrollCta?: ScrollCta;
  /** Whether "Request more info" is the primary button. */
  requestInfoPrimary?: boolean;
};

/**
 * The call-to-action row under the product summary. Which buttons appear and
 * which one is primary is decided by the composing page, since flooring
 * (samples, room visualizer) and KB&F (3D configurator) differ.
 */
export const ProductDetailsCtas = ({
  productInfo,
  showAddToCart = false,
  showOrderSample = false,
  scrollCta,
  requestInfoPrimary = false,
}: ProductDetailsCtasProps) => {
  const t = useTranslations('products');

  return (
    <div className="pdp__ctas ui-flex ui-gap-2 ui-flex-wrap !ui-mt-8">
      {showAddToCart && <AddToCart productInfo={productInfo} />}

      {showOrderSample && (
        <Samples
          productInfo={productInfo}
          samplePrice={productInfo.samplePrice}
        />
      )}

      {scrollCta && (
        <BetterButton
          label={scrollCta.label}
          labelClassName="ui-text-sm"
          btnStyle={scrollCta.primary ? 'primary' : 'neutral'}
          attributes={{
            id: scrollCta.id,
            onClick: () => scrollToElement(scrollCta.targetId),
          }}
        />
      )}

      <BetterButton
        label={t('requestMoreInfo')}
        labelClassName="ui-text-sm"
        btnStyle={requestInfoPrimary ? 'primary' : 'neutral'}
        attributes={{
          onClick: () => scrollToElement('request-form'),
        }}
      />
    </div>
  );
};
